import mapboxgl from 'mapbox-gl';

import { MapStyle } from 'appConstants';
import { projectStatusChipVariants } from 'features/user_admin/ProjectStatusBadge';
import { primary_text_color, stl_background_color } from 'theme/cemTheme';

import { getRoadClassLayerFilter } from './mapCommon';
import {
  MIN_MAP_ZOOM,
  baseSegmentColor,
  baseSegmentColorLm,
  bookmarkMarkerColor,
  bookmarkMarkerColorLm,
  border_thick_line_width_expression,
  hoveredRouteColor,
  hoveredRouteColorLm,
  hoveredSegmentColor,
  hoveredSegmentColorLm,
  kArrowLayoutBase,
  kBookmarkLayerId,
  kBookmarkMarkerImg,
  kHalfDirectionArrowNameRHD,
  kHoveredDirectionArrowName,
  kHoveredSegmentLayerId,
  kHoveredSegmentSourceId,
  kHoveredSegmentSymbolsLayerId,
  kMainVisLineOpacityExpression,
  kMainVisLineOpacitySegmentSelected,
  kMonitorRouteBorderLayerId,
  kMonitorRouteLayerId,
  kMonitorSymbolBgId,
  kMonitorSymbolId,
  kProjectBoundaryLayerId,
  kProjectMaskLayerId,
  kQueuePositionMarkerImg,
  kQueueStartMarkerImg,
  kRouteArrowsBgLayerId,
  kRouteArrowsLayerId,
  kSegmentLayerId,
  kSegmentLayerSourceLayerId,
  kSegmentSourceId,
  kSegmentSymbolsLayerId,
  kSelectedRouteLayerId,
  kSelectedSegmentLayerId,
  kSelectedSegmentSymbolsLayerId,
  kSolidDotImg,
  kStarMarker,
  kSubscriptionBoundaryFadeLayerId,
  kSubscriptionBoundaryLineLayerId,
  kSubscriptionBoundarySourceId,
  kSubscriptionProjectFillLayerId,
  kSubscriptionProjectLabelsLayerId,
  kSubscriptionProjectLabelsSourceId,
  kSubscriptionProjectLineLayerId,
  kSubscriptionProjectSourceId,
  kViewOnlySegmentLayerId,
  line_width_expression,
  make_line_width_expression,
  make_symbol_width_expression,
  mapHighContrastColorLm,
  mapMidContrastColorLm,
  projectBorderColor,
  projectBorderColorLm,
  rmRouteColor,
  rmRouteColorLm,
  selectedRouteColor,
  selectedRouteColorLm,
  selectedSegmentColor,
  selectedSegmentColorLm,
  selection_line_width_expression,
  thick_line_width_expression,
  viewOnlyBaseSegmentColor,
  viewOnlyBaseSegmentColorLm,
} from './map_constants';

export function isLm(mapStyle: MapStyle) {
  return mapStyle === MapStyle.Light;
}
export function mapBg(mapStyle: MapStyle) {
  return isLm(mapStyle) ? 'white' : stl_background_color;
}

export function mapBgDimmed(mapStyle: MapStyle, opacity: number) {
  return isLm(mapStyle)
    ? `rgba(255,255,255,${opacity})`
    : `rgba(37,36,36,${opacity})`;
}

export function mapMidContrastColor(mapStyle: MapStyle) {
  return isLm(mapStyle) ? mapMidContrastColorLm : primary_text_color;
}

export function mapHighContrastColor(mapStyle: MapStyle) {
  return isLm(mapStyle) ? mapHighContrastColorLm : primary_text_color;
}

export const kProjectBoundaryLayerDefinition: mapboxgl.LineLayer = {
  id: kProjectBoundaryLayerId,
  type: 'line',
  source: kProjectBoundaryLayerId,
  paint: {
    'line-color': projectBorderColor,
    'line-width': selection_line_width_expression,
    'line-dasharray': [
      'step',
      ['zoom'],
      ['literal', [3, 2, 5]],
      7,
      ['literal', [2, 1.5]],
    ],
  },
};

export function getProjectMaskLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.FillLayer {
  return {
    id: kProjectMaskLayerId,
    type: 'fill',
    source: kProjectMaskLayerId,
    paint: {
      'fill-color': isLm(mapStyle) ? 'lightgrey' : 'black',
      'fill-opacity': 0.5,
    },
  };
}

export function getSelectedSegmentLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.LineLayer {
  return {
    id: kSelectedSegmentLayerId,
    type: 'line',
    source: kSelectedSegmentLayerId,
    layout: {
      'line-cap': 'round',
    },
    paint: {
      'line-color': isLm(mapStyle)
        ? selectedSegmentColorLm
        : selectedSegmentColor,
      'line-width': selection_line_width_expression,
    },
  };
}

export function getSelectedSegmentLayerArrowDefinition(
  mapStyle: MapStyle,
): mapboxgl.SymbolLayer {
  return {
    id: kSelectedSegmentSymbolsLayerId,
    type: 'symbol',
    source: kSelectedSegmentLayerId,
    layout: {
      ...kArrowLayoutBase,
      'icon-image': kHoveredDirectionArrowName,
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_MAP_ZOOM,
        1.6,
        14,
        1.6,
        16,
        3.2,
        19,
        4,
      ],
    },
    paint: {
      'icon-color': isLm(mapStyle)
        ? selectedSegmentColorLm
        : selectedSegmentColor,
      'icon-opacity': 1,
    },
  };
}

export function getMonitorSymbolLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.SymbolLayer {
  return {
    id: kMonitorSymbolId,
    type: 'symbol',
    source: kMonitorSymbolId,
    layout: {
      ...kArrowLayoutBase,
      'icon-image': kStarMarker,
      'icon-size': make_symbol_width_expression(0.1),
    },
    paint: {
      'icon-color': mapMidContrastColor(mapStyle),
      'icon-opacity': ['case', ['boolean', ['get', 'isStarred'], false], 1, 0],
    },
  };
}
export function getMonitorSymbolBgLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.SymbolLayer {
  return {
    id: kMonitorSymbolBgId,
    type: 'symbol',
    source: kMonitorSymbolId,
    layout: {
      ...kArrowLayoutBase,
      'icon-image': kSolidDotImg,
      'icon-size': make_symbol_width_expression(0.2),
    },
    paint: {
      'icon-color': mapBg(mapStyle),
      'icon-opacity': ['case', ['boolean', ['get', 'isStarred'], false], 1, 0],
    },
  };
}

export function getHoveredSegmentLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.LineLayer {
  return {
    id: kHoveredSegmentLayerId,
    type: 'line',
    source: kHoveredSegmentSourceId,
    layout: {
      'line-cap': 'round',
    },
    paint: {
      'line-color': isLm(mapStyle)
        ? hoveredSegmentColorLm
        : hoveredSegmentColor,
      'line-width': make_line_width_expression(2.0),
    },
  };
}

export function getHoveredSegmentLayerArrowDefinition(
  mapStyle: MapStyle,
): mapboxgl.SymbolLayer {
  return {
    id: kHoveredSegmentSymbolsLayerId,
    type: 'symbol',
    source: kHoveredSegmentLayerId,
    layout: kArrowLayoutBase,
    paint: {
      'icon-color': isLm(mapStyle)
        ? hoveredSegmentColorLm
        : hoveredSegmentColor,
      'icon-opacity': 1,
    },
  };
}

export const kSegmentLayerArrowDefinition: mapboxgl.SymbolLayer = {
  id: kSegmentSymbolsLayerId,
  type: 'symbol',
  source: kSegmentSourceId,
  'source-layer': kSegmentLayerSourceLayerId,
  layout: {
    ...kArrowLayoutBase,
    'icon-image': kHalfDirectionArrowNameRHD,
    'icon-allow-overlap': false,
  },
};

export function getSelectedRouteLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.LineLayer {
  return {
    id: kSelectedRouteLayerId,
    type: 'line',
    source: kSelectedRouteLayerId,
    layout: {
      'line-cap': 'round',
    },
    paint: {
      'line-color': isLm(mapStyle) ? selectedRouteColorLm : selectedRouteColor,
      // 'line-color': 'red',
      'line-width': selection_line_width_expression,
    },
  };
}

export function getSegmentLayerDefinition(
  mapStyle: MapStyle,
  roadFilters?: string[] | null,
): mapboxgl.LineLayer {
  const layer: mapboxgl.LineLayer = {
    id: kSegmentLayerId,
    type: 'line',
    source: kSegmentSourceId,
    layout: {},
    paint: {
      'line-color': isLm(mapStyle) ? baseSegmentColorLm : baseSegmentColor,
      'line-width': line_width_expression,
      'line-opacity': kMainVisLineOpacityExpression(
        { [MapStyle.Dark]: 0.7, [MapStyle.Sat]: 1.0, [MapStyle.Light]: 0.6 }[
          mapStyle
        ] || 0.7,
      ),
    },
  };
  const roadFilter = getRoadClassLayerFilter(roadFilters);
  if (roadFilter) layer.filter = roadFilter;
  return layer;
}

export function getRouteMonitorLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.LineLayer {
  return {
    id: kMonitorRouteLayerId,
    type: 'line',
    source: kMonitorRouteLayerId,
    layout: {
      'line-cap': [
        'case',
        ['boolean', ['get', 'isSelected'], false],
        'square',
        'round',
      ],
    },
    paint: {
      'line-color': isLm(mapStyle) ? rmRouteColorLm : rmRouteColor,
      'line-opacity': [
        'case',
        ['boolean', ['get', 'isHovered'], false],
        1,
        isLm(mapStyle) ? 0.5 : 0.8,
      ],
      'line-width': thick_line_width_expression,
    },
  };
}

export function getRouteMonitorBorderLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.LineLayer {
  return {
    id: kMonitorRouteBorderLayerId,
    type: 'line',
    layout: {
      'line-cap': 'butt',
    },
    paint: {
      'line-color': isLm(mapStyle) ? hoveredRouteColorLm : hoveredRouteColor,
      'line-opacity': ['case', ['boolean', ['get', 'isHovered'], false], 1, 0],
      'line-width': border_thick_line_width_expression,
    },
  };
}

function icon_width_expression(basis_width: number): mapboxgl.Expression {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    MIN_MAP_ZOOM,
    5 * basis_width,
    14,
    5 * basis_width,
    16,
    8 * basis_width,
    19,
    20 * basis_width,
  ];
}

export function getRouteArrowBgLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.SymbolLayer {
  return {
    id: kRouteArrowsBgLayerId,
    type: 'symbol',
    layout: {
      ...kArrowLayoutBase,
      'icon-image': kSolidDotImg,
      'icon-size': icon_width_expression(0.25),
    },
    paint: {
      'icon-color': mapBg(mapStyle),
      'icon-opacity': 1,
    },
    filter: ['in', ['get', 'role'], ['literal', ['start', 'end']]],
  };
}

export function getRouteArrowLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.SymbolLayer {
  return {
    id: kRouteArrowsLayerId,
    type: 'symbol',
    layout: {
      ...kArrowLayoutBase,
      'icon-image': [
        'match',
        ['get', 'role'],
        'start',
        kQueueStartMarkerImg,
        'end',
        kQueuePositionMarkerImg,
        kQueuePositionMarkerImg,
      ],
      'icon-size': icon_width_expression(0.15),
    },
    paint: {
      'icon-color': mapHighContrastColor(mapStyle),
      'icon-opacity': 1,
    },
    filter: ['in', ['get', 'role'], ['literal', ['start', 'end']]],
  };
}

export function getViewOnlySegmentLayerDefinition(
  mapStyle: MapStyle,
  roadFilters?: string[] | null,
): mapboxgl.LineLayer {
  const layer: mapboxgl.LineLayer = {
    id: kViewOnlySegmentLayerId,
    type: 'line',
    source: kSegmentSourceId,
    layout: {},
    paint: {
      'line-color': isLm(mapStyle)
        ? viewOnlyBaseSegmentColorLm
        : viewOnlyBaseSegmentColor,
      'line-width': line_width_expression,
      'line-opacity': kMainVisLineOpacityExpression(
        kMainVisLineOpacitySegmentSelected,
      ),
    },
  };
  const roadFilter = getRoadClassLayerFilter(roadFilters);
  if (roadFilter) layer.filter = roadFilter;
  return layer;
}
export function getBookmarkLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.SymbolLayer {
  return {
    id: kBookmarkLayerId,
    type: 'symbol',
    source: kBookmarkLayerId,
    layout: {
      'icon-image': kBookmarkMarkerImg,
      'icon-allow-overlap': false,
      'icon-size': 2,
    },
    paint: {
      'icon-color': isLm(mapStyle)
        ? bookmarkMarkerColorLm
        : bookmarkMarkerColor,
      'icon-opacity': 1,
    },
  };
}

export function getSubscriptionBoundaryFadeLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.FillLayer {
  return {
    id: kSubscriptionBoundaryFadeLayerId,
    type: 'fill',
    source: kSubscriptionBoundarySourceId,
    layout: {},
    paint: {
      'fill-color': mapBg(mapStyle),
      'fill-opacity': 0.7,
    },
  };
}

export function getSubscriptionBoundaryLineLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.LineLayer {
  return {
    id: kSubscriptionBoundaryLineLayerId,
    type: 'line',
    source: kSubscriptionBoundarySourceId,
    layout: {},
    paint: {
      'line-width': 1,
      'line-color': isLm(mapStyle) ? projectBorderColorLm : projectBorderColor,
    },
  };
}
const kProjectStatusColorMapping: mapboxgl.Expression = [
  'match',
  ['downcase', ['get', 'status']],
  'fetching',
  projectStatusChipVariants.fetching.color.icon,
  'draft',
  projectStatusChipVariants.draft.color.icon,
  'pending_review',
  projectStatusChipVariants.pending_review.color.icon,
  'active',
  projectStatusChipVariants.active.color.icon,
  'inactive',
  projectStatusChipVariants.inactive.color.icon,
  projectStatusChipVariants.active.color.icon,
];

export function getSubscriptionProjectsLineLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.LineLayer {
  return {
    id: kSubscriptionProjectLineLayerId,
    type: 'line',
    source: kSubscriptionProjectSourceId,
    layout: {},
    paint: {
      'line-width': 2,
      'line-color': kProjectStatusColorMapping,
      'line-dasharray': [0, 2, 2],
    },
  };
}

export function getSubscriptionProjectsFillLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.FillLayer {
  return {
    id: kSubscriptionProjectFillLayerId,
    type: 'fill',
    source: kSubscriptionProjectSourceId,
    layout: {},
    paint: {
      'fill-color': kProjectStatusColorMapping,
      'fill-opacity': 0.15,
    },
  };
}

export function getSubscriptionProjectsLabelLayerDefinition(
  mapStyle: MapStyle,
): mapboxgl.SymbolLayer {
  return {
    id: kSubscriptionProjectLabelsLayerId,
    type: 'symbol',
    source: kSubscriptionProjectLabelsSourceId,
    layout: { 'text-field': ['get', 'name'] },
    paint: {
      'text-color': mapHighContrastColor(mapStyle),
      'text-halo-color': mapBg(mapStyle),
      'text-halo-blur': 10,
    },
  };
}
